/**
 * Default View options shared between the user Profile "Default View" card and
 * the admin Edit-User dialog so the two screens stay in sync.
 *
 * The value is stored at user.preferences.default_view and consumed by
 * DefaultViewRedirect to choose the landing screen after login.
 */

export const DEFAULT_VIEW_OPTIONS = [
  {
    value: 'classic_user',
    label: 'Classic User Dashboard',
    helperText: 'Standard user dashboard with observations, projects, and files',
  },
  {
    value: 'classic_admin',
    label: 'Classic Admin Dashboard',
    helperText: 'Admin dashboard with system overview and management tools',
  },
  {
    value: 'control_center',
    label: 'Admin Control Center',
    helperText: 'Single-pane admin view of observatories, users, analytics, and infrastructure',
  },
  {
    value: 'service_center',
    label: 'Service Center',
    helperText: 'Server-admin support tools — respond to support requests',
  },
  {
    value: 'operations_control',
    label: 'Operations Control',
    helperText: 'Mission control view with live telemetry, queue, and timeline',
  },
];

export const DEFAULT_VIEW_VALUES = DEFAULT_VIEW_OPTIONS.map(o => o.value);

/**
 * Determine which default-view options should be offered for a TARGET user,
 * based on their privileges. Used by the admin Edit-User dialog.
 *
 * - Server administrators: only the two admin views.
 * - Observatory admins / owners (and org admins/owners): all four views.
 * - Everyone else: only the two non-admin views.
 *
 * @param {{ isServerAdmin?: boolean, hasObservatoryAdmin?: boolean }} opts
 * @returns {string[]} allowed default_view values
 */
export function getAllowedDefaultViews({ isServerAdmin, hasObservatoryAdmin } = {}) {
  // Service Center is server-admin only, so it is offered exclusively to server
  // admins (never to observatory/org admins, who would hit Access Denied).
  if (isServerAdmin) return ['classic_admin', 'control_center', 'service_center'];
  if (hasObservatoryAdmin) return ['classic_user', 'classic_admin', 'control_center', 'operations_control'];
  return ['classic_user', 'operations_control'];
}
